/**
 * Raster der Uebertragungs-Gesamtuebersicht (D-03, D-05, D-07; Sketch 025 Variante C `renderC()`/
 * `cellBtn()`): je Auswertung eine Zeile, je Zeitraum ein Feld. Monatliche Auswertungen zeigen
 * zwoelf Felder, vierteljaehrliche vier Felder ueber je drei Spalten, jaehrliche ein breites Feld
 * ueber alle zwoelf Spalten mit dem langen "Uebertragen am ... " Text. Gruppenzeilen gliedern nach
 * Bereich. Zustand, Beschriftung und Klickbarkeit jeder Zelle kommen fertig vom Server
 * (`TransferGridCell`, 41-06) -- keine Ableitung im Frontend.
 *
 * "Noch nie uebertragen" (`status === 'never'`) ist nicht klickbar (D-03).
 */
import { Fragment, type CSSProperties, type ReactNode } from 'react';
import { statusPalette } from '../../theme/colors';
import type {
  TransferCellStatus,
  TransferGridCell,
  TransferGridGroup,
  TransferGridRow,
} from '../../types/statistikPortalTransfer';
import classes from './TransferGrid.module.css';

const MONTH_ABBREVIATIONS = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
];

const gridColorVars = {
  '--cell-transferred-bg': statusPalette.freigegeben.bg,
  '--cell-transferred-border': statusPalette.freigegeben.border,
  '--cell-transferred-fg': statusPalette.freigegeben.fg,
  '--cell-changed-bg': statusPalette.eingereicht.bg,
  '--cell-changed-border': statusPalette.eingereicht.border,
  '--cell-changed-fg': statusPalette.eingereicht.fg,
  '--cell-failed-border': statusPalette.fehler.border,
  '--cell-failed-fg': statusPalette.fehler.fg,
} as CSSProperties;

const STATUS_CLASS: Record<TransferCellStatus, string> = {
  transferred: classes.cellTransferred,
  changed: classes.cellChanged,
  failed: classes.cellFailed,
  never: classes.cellNever,
};

const LEGEND_SWATCH_CLASS: Record<TransferCellStatus, string> = {
  transferred: classes.legendSwatchTransferred,
  changed: classes.legendSwatchChanged,
  failed: classes.legendSwatchFailed,
  never: classes.legendSwatchNever,
};

function cx(...values: Array<string | false | undefined>): string {
  return values.filter(Boolean).join(' ');
}

export interface TransferGridSelection {
  evaluationType: string;
  period: number;
}

export interface TransferGridProps {
  groups: TransferGridGroup[];
  selectedCell: TransferGridSelection | null;
  onSelectCell: (selection: TransferGridSelection) => void;
}

function CellButton({
  evaluationType,
  cell,
  wide,
  selected,
  onSelectCell,
}: {
  evaluationType: string;
  cell: TransferGridCell;
  wide: boolean;
  selected: boolean;
  onSelectCell: (selection: TransferGridSelection) => void;
}) {
  return (
    <button
      type="button"
      className={cx(
        classes.cell,
        STATUS_CLASS[cell.status],
        wide && classes.cellWide,
        selected && classes.cellSelected
      )}
      disabled={!cell.clickable}
      aria-pressed={selected}
      data-status={cell.status}
      title={`${cell.periodLabel}: ${cell.label}`}
      onClick={() => {
        if (cell.clickable) {
          onSelectCell({ evaluationType, period: cell.period });
        }
      }}
    >
      {wide ? cell.label : cell.shortLabel}
    </button>
  );
}

function rowCells(
  row: TransferGridRow,
  selectedCell: TransferGridSelection | null,
  onSelectCell: (selection: TransferGridSelection) => void
): ReactNode {
  const isSelected = (cell: TransferGridCell) =>
    selectedCell !== null &&
    selectedCell.evaluationType === row.evaluationType &&
    selectedCell.period === cell.period;

  if (row.interval === 'yearly') {
    const cell = row.cells[0];
    return (
      <td colSpan={12}>
        <CellButton
          evaluationType={row.evaluationType}
          cell={cell}
          wide
          selected={isSelected(cell)}
          onSelectCell={onSelectCell}
        />
      </td>
    );
  }

  const colSpan = row.interval === 'quarterly' ? 3 : 1;
  return row.cells.map((cell) => (
    <td key={cell.period} colSpan={colSpan}>
      <CellButton
        evaluationType={row.evaluationType}
        cell={cell}
        wide={false}
        selected={isSelected(cell)}
        onSelectCell={onSelectCell}
      />
    </td>
  ));
}

export function TransferGrid({ groups, selectedCell, onSelectCell }: TransferGridProps) {
  return (
    <div style={gridColorVars}>
      <div className={classes.gridWrap}>
        <table className={classes.raster}>
          <colgroup>
            <col className={classes.labelCol} />
          </colgroup>
          <thead>
            <tr>
              <th className={classes.headCell} />
              {MONTH_ABBREVIATIONS.map((month) => (
                <th key={month} className={classes.headCell}>
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.area}>
                <tr>
                  <th colSpan={13} className={classes.groupLabel}>
                    {group.label}
                  </th>
                </tr>
                {group.rows.map((row) => (
                  <tr key={row.evaluationType}>
                    <th className={classes.rowLabel}>
                      {row.label}
                      {row.yearLabel && <span className={classes.yearLabel}>{row.yearLabel}</span>}
                    </th>
                    {rowCells(row, selectedCell, onSelectCell)}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className={classes.legend}>
        <span>
          <i className={cx(classes.legendSwatch, LEGEND_SWATCH_CLASS.transferred)} />
          übertragen
        </span>
        <span>
          <i className={cx(classes.legendSwatch, LEGEND_SWATCH_CLASS.changed)} />
          seitdem geändert
        </span>
        <span>
          <i className={cx(classes.legendSwatch, LEGEND_SWATCH_CLASS.failed)} />
          letzter Versuch fehlgeschlagen
        </span>
        <span>
          <i className={cx(classes.legendSwatch, LEGEND_SWATCH_CLASS.never)} />
          noch nie übertragen
        </span>
        <span>Feld anklicken: Verlauf dieses Zeitraums</span>
      </div>
    </div>
  );
}
